import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { countryList, s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { airconCostCalculation } from '../../../js/aircon-cost'
import Link from 'next/link'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198

    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    const currencyCd = "SGD"

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

    const getLocalityList = () => {
        for(let x in countryList){
            if(countryList[x].country_name === countryName){
                return countryList[x].localityList
            }
        }
    } 

    const localityList = getLocalityList()

  return {
      props: {
        countryName,
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        currencyCd,
        wpNewPosts,
        wpPosts,
        localityList
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const requestType = 'Aircon Services'
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const requestCategorySettings = props.requestCategorySettings;  
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const localityList = props.localityList
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Common Reasons For Aircon Not Blowing Cold Air | Repair & Cost</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Is your air conditioner not blowing cold air? This is a very common problem in Singapore. Find out the most common reasons and how to fix them."/>
        <meta name="keywords" content="Aircon not cold"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Common Reasons For Aircon Not Blowing Cold Air | Repair & Cost`}/>
        <meta property="og:description" content="Is your air conditioner not blowing cold air? This is a very common problem in Singapore. Find out the most common reasons and how to fix them." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-28T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/broken-aircon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="hhttps://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/broken-aircon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling"/>
        <meta name="twitter:title" content={`Common Reasons For Aircon Not Blowing Cold Air | Repair & Cost`}/> 
        <meta name="twitter:description" content="Is your air conditioner not blowing cold air? This is a very common problem in Singapore. Find out the most common reasons and how to fix them."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling"},"headline":"Common Reasons For Aircon Not Cooling","description":"Is your air conditioner not blowing cold air? This is a very common problem in Singapore. Find out the most common reasons and how to fix them.","image":["hhttps://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/broken-aircon.png"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-28T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling","name":"Aircon Not Cooling","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling"}}]}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-not-cold" className='relative'>
                    <AirconRepair 
                        setRequestForm={setRequestForm} 
                        setIsQuickBook={setIsQuickBook} 
                        title={"Common Reasons for Aircon not blowing cold air & How to Fix"} 
                        problem={"Aircon is not cold"}
                        heroImage="blog/living/aircon/chinese-aircon-service-man-smiling.jpg"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-10 px-2 leading-8'>
                <div className='space-y-5'>
                    <p>
                        In the sweltering heat of Singapore, a malfunctioning air conditioning system can quickly turn your home into an uncomfortable environment. If you find 
                        that your aircon isn&apos;t providing the cooling relief you need, there could be various reasons behind this issue. It is important to understand the common 
                        factors that can cause your air con to stop blowing cold air, so that your air conditioning system can be fixed appropriately without spending on 
                        unwanted repair expenses.
                    </p>
                    <div className='relative h-96 my-2'>
                        <Image className='rounded-lg'
                            src={s3Static + "blog/living/broken-aircon.png"}
                            fill
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon not cold'
                        />          
                    </div>
                </div>
                <div className='py-2 space-y-2'>
                    <h2 className='pt-5 font-bold text-2xl md:text-3xl'>9 Common Reasons For Aircon Not Cold</h2>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>1. Dirty or Clogged Air Filters</h3>
                        <p>
                            One of the most prevalent reasons for reduced cooling efficiency is dirty or clogged air filters. Singapore&apos;s humid and dusty conditions can 
                            cause filters to become quickly congested with particles, obstructing proper airflow and heat exchange. Regularly cleaning or replacing air 
                            filters every 1 to 3 months is essential to maintain optimal cooling performance.
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/dirty-aircon-filters.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover'}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='dirty aircon filters'
                            />          
                        </div>
                        <p>
                            <strong>Solution: </strong>Take out the aircon air filter and thoroughly clean them under water and dry them with a cloth before placing it back in the aircon. 
                            If the dirt is rigid, you may need a special chemical cleaner to remove it. 
                        </p>
                        <p>
                            <strong>Cost: </strong>No cost if you can do it yourself. Getting an HVAC technician to conduct a general servicing can cost anywhere between 
                            $19 - $25 per aircon unit. 
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/cleaning-aircon-filter.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover', objectPosition: "left"}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='cleaning aircon filters under water'
                            />          
                        </div>
                    </div>
                    
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>2. Excessive Dust and Debris on Evaporator Coil</h3>
                        <p>
                            Accumulated dust and debris on the evaporator coil can insulate it, preventing efficient heat exchange leading to inefficient cooling.
                            This can be prevented with regular aircon servicing to keep the evaporator coil clean.
                        </p>                        
                        <p>
                            <strong>Solution: </strong>You would need a chemical wash or chemical overhaul depending on the extent of the debris. This can only be executed 
                            by trained aircon technician, so do not try this yourself as the chemical used can caused harm to you and the system if not used as intended.
                        </p>
                        <p>
                            <strong>Cost: </strong><span>Check pricing and book here - </span>
                            <Link href="/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul" prefetch={false} className='text-orange-600'>
                                    Chemical Wash and Chemical Overhaul
                            </Link>
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/dirty-aircon-evaporator-coil.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover', objectPosition: "top"}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='dirty aircon evaporator coil causing water leakage'
                            />          
                        </div> 
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>3. Low Refrigerant Levels or Gas Leakage</h3>
                        <p>
                            Refrigerant is the lifeblood of your air conditioning system, responsible for absorbing and releasing heat. Low refrigerant levels can 
                            hinder the cooling process, resulting in inadequate temperature regulation. If you suspect a refrigerant leak, it&apos;s imperative to contact a 
                            professional technician to assess and replenish the refrigerant.
                        </p>                        
                        <p>
                            <strong>Solution: </strong>If your aircon has not received a refrigerant gas refill for a long time then it might be low on gas. Refilling the gas
                            in the condenser will to get the cool air again. If the refrigerant gas level is low after a recent refill, then it may be because of gas leakage.
                            <Link href="/sg/aircon-servicing-singapore/aircon-gas-leakage" className='text-orange-600'> Refrigerant gas leakage</Link> is the hardest to spot and 
                            fix with a lot of uncertainty, in most cases it is recommended to replace the entire air condition system
                            with a new one. 
                        </p>
                        <p>
                            <strong>Cost: </strong><span>Check gas refill pricing and book here - </span>
                            <Link href="/sg/aircon-servicing-singapore/aircon-gas-top-up" prefetch={false} className='text-orange-600'>
                                Aircon Gas Top Up
                            </Link>
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/aircon-gas.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover', objectPosition: "top"}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='aircon refrigerant gas leak'
                            />          
                        </div> 
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>4. <Link href="/sg/aircon-servicing-singapore/aircon-thermostat" className="text-orange-600">Faulty Thermostat</Link></h3>
                        <p>
                            The thermostat is the control center of your aircon, dictating when the cooling cycle begins and ends. A malfunctioning thermostat can lead to 
                            inaccurate temperature readings and inconsistent cooling. Calibrating or replacing the thermostat can help restore proper cooling function.
                        </p>
                        <p>
                            <strong>Solution: </strong>In most cases it is advised to install a new thermostat.
                        </p>
                        <p>
                            <strong>Cost: </strong>Estimated cost is anywhere between $100 to $250 for the thermostat itself and an additional $50 to $150 for labor charges.
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/aircon/aircon-thermostat.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover', objectPosition: "top"}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='faulty thermostat causing the aircon to blow warm air'
                            />          
                        </div>
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>5. Malfunctioning or Blocked Condenser Unit</h3>
                        <p>
                            The condenser unit, usually located outside, releases the heat absorbed from your home&apos;s interior. If the condenser fins are obstructed by dirt, 
                            debris, or vegetation, it can impede heat dissipation and reduce cooling efficiency. Regularly cleaning the condenser unit and ensuring adequate 
                            clearance can alleviate this issue.
                        </p>
                        <p>
                            Malfunctioning of the condenser components like the 
                            <Link href="/sg/aircon-servicing-singapore/condenser-fan-motor" className="text-orange-600"> condenser motor</Link>
                            , the 
                            <Link href="/sg/aircon-servicing-singapore/ac-printed-circuit-board-pcb" className="text-orange-600"> PCB </Link>
                            or 
                            <Link href="/sg/aircon-servicing-singapore/aircon-capacitor" className="text-orange-600"> capacitor</Link>
                            , can also result in improper or no cooling. Get an air conditioner technician to troubleshoot and fix it for you.
                        </p>
                        
                        <p>
                            <strong>Solution: </strong>The solution depends on the root cause of the problem. Submit a request for 
                            <Link href="/sg/aircon-servicing-singapore/aircon-repair" className='text-orange-600'> aircon repair </Link> with us. Our technician
                            will visit your premises to troubleshoot and identify the root cause of the problem and advise on the solution.
                        </p>
                        <p>
                            <strong>Cost: </strong>The actual cost will vary based on the root cause or the problem, which can only be identified after the technician has 
                            completed the troubleshooting.
                        </p>
                        <div className='relative h-96 my-2'>
                            <Image className='rounded-lg'
                                src={s3Static + "blog/living/air-conditioner-condenser-cleaning.jpg"}
                                fill
                                sizes="(max-width: 768px) 100vw, 33vw"
                                style={{objectFit: 'cover'}}
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='aircon condenser kept outdoor'
                            />          
                        </div>
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>6. <Link href="/sg/aircon-servicing-singapore/aircon-thermistor-sensor" className='text-orange-600'>Faulty Thermistor Sensor</Link></h3>
                        <p>
                            The thermistor sensor helps the air conditioning system to regulate the cooling or heating process by tracking the temperature of the indoor aircon unit.
                            A malfunctioning thermistor it will provide inaccurate reading to the A/C system, hence will impact the cooling mechanism.
                        </p>
                        <p>
                            <strong>Solution: </strong>The thermistor needs to be replace with a new one.
                        </p>
                        <p>
                            <strong>Cost: </strong>Thermistor replacement can cost anywhere between $120 to $250 including the material, transport and technician labor cost.
                        </p>
                    </div>                    
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>7. Inadequate Maintenance</h3>
                        <p>
                            Like any complex system, air conditioners require regular maintenance to perform optimally. Over time, components may wear out, electrical 
                            connections may loosen, and efficiency may decline. Scheduling annual professional maintenance can address potential issues before they 
                            disrupt your aircon&apos;s cooling capabilities.
                        </p>
                        <p>
                            <strong>Solution: </strong>Get a annual servicing package, so that a general cleaning and maintenance of the aircon units can be conducted on a regular basis.
                        </p>
                        <p>
                            <strong>Cost: </strong><span>Check pricing and book here - </span>
                            <Link href="/sg/aircon-servicing-singapore/annual-package" prefetch={false} className='text-orange-600'>
                                Annual Servicing Package
                            </Link>
                        </p>
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>8. Insufficient Airflow</h3>
                        <p>
                            Blocked vents, obstructions around the indoor unit, or a malfunctioning fan can lead to inadequate airflow. Insufficient airflow inhibits heat 
                            exchange and cooling efficiency. Regularly clean and clear vents, and ensure there&apos;s proper clearance around the indoor unit.
                        </p>
                        <p>
                            <strong>Solution: </strong>Remove any blockages and obstructions around the aircon units.
                        </p>
                        <p>
                            <strong>Cost: </strong>No Cost involved
                        </p>
                    </div>
                    <div className='space-y-2'>
                        <h3 className='font-bold pt-3 text-lg'>9. Age of Air Conditioning System</h3>
                        <p>
                            As air conditioning systems age, their efficiency and performance naturally decline. Older units may struggle to cool effectively due to worn-out components, outdated technology, or accumulated wear and tear. If your aircon is more than 10-15 years old, it may be time to consider a replacement.
                        </p>
                        <p>
                            <strong>Solution: </strong>Upgrade to a newer, energy-efficient air conditioning system to restore optimal cooling and reduce energy costs.
                        </p>
                        <p>
                            <strong>Cost: </strong>The cost of a new aircon system varies widely based on brand, capacity, and installation requirements, typically ranging from $1,000 to $3,000 or more.
                        </p>
                    </div>                    
                    <div className='py-5'>
                        <p className='p-5 border rounded-lg italic text-sm leading-8'>
                            Maintaining a cool and comfortable indoor environment in Singapore is essential for your well-being and daily life. If your aircon isn&apos;t delivering 
                            the desired level of cold air, don&apos;t let the heat get the best of you. By addressing common issues like dirty air filters, refrigerant leaks, faulty 
                            thermostats, and other maintenance-related factors, you can troubleshoot and restore your air conditioning system&apos;s cooling capabilities. Remember, 
                            for more complex issues or when in doubt, it&apos;s always best to consult a professional HVAC technician in Singapore to diagnose and rectify the problem 
                            effectively.
                        </p>
                    </div>
                </div> 
            </div>
            {/* <AcServiceLocality localityList={localityList}/> */}
            {/* <AirconBacklink/> */}
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
            {/* <AirconQuickLinks/> */}
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
